import type { CSSProperties, ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { reorderIds } from "@shared/catalogue-order";
import type { CatalogueItem } from "../template/CatalogueGrid";
import { luminance } from "../template/CatalogueGrid";
import { tokens } from "../../theme";
import { SegmentedChoice } from "./StartNavLayout";

/**
 * A per-place on/off list over a global catalogue, as the design draws it
 * (NendaPortal_1 "Appar" 1677:13245 / "Radio" 1694:14864): a drag handle, the
 * brand's logo, its name and one line under it, and Yes / No.
 *
 * Replaces the tile grid inside Start & navigation. The data is unchanged -
 * `isOn`/`onToggle` over a disabled-ids list, `onReorder` with the FULL id list
 * in its new order, `pinnedBy` for an entry whose position is not the
 * operator's (NFLX-4: a pinned row has no handle but keeps its Yes / No).
 */
export default function CatalogueList({
  items,
  isOn,
  onToggle,
  onReorder,
  pinnedBy,
  subtitle,
  trailing,
  "data-testid": testId,
}: {
  items: CatalogueItem[];
  isOn: (id: string) => boolean;
  onToggle: (id: string) => void;
  onReorder?: (ids: string[]) => void;
  pinnedBy?: ReadonlyMap<string, string>;
  /** The line under the name; defaults to the entry's tag. */
  subtitle?: (item: CatalogueItem) => string | undefined;
  /** Extra controls at the end of a row (edit / delete on a custom channel). */
  trailing?: (item: CatalogueItem) => ReactNode;
  "data-testid"?: string;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const movable = items.filter((i) => !pinnedBy?.has(i.id)).map((i) => i.id);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || !onReorder) return;
    const next = reorderIds(
      items.map((i) => i.id),
      String(active.id),
      String(over.id)
    );
    if (next) onReorder(next);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={movable} strategy={verticalListSortingStrategy}>
        <Box
          data-testid={testId}
          sx={{ borderTop: "1px solid", borderColor: tokens.divider }}
        >
          {items.map((item) => (
            <Row
              key={item.id}
              item={item}
              on={isOn(item.id)}
              onToggle={() => onToggle(item.id)}
              draggable={!!onReorder && !pinnedBy?.has(item.id)}
              subtitle={
                pinnedBy?.get(item.id) ?? (subtitle ? subtitle(item) : item.tag)
              }
              trailing={trailing?.(item)}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
}

function Row({
  item,
  on,
  onToggle,
  draggable,
  subtitle,
  trailing,
}: {
  item: CatalogueItem;
  on: boolean;
  onToggle: () => void;
  draggable: boolean;
  subtitle?: string;
  trailing?: ReactNode;
}) {
  const s = useSortable({ id: item.id, disabled: !draggable });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(s.transform),
    transition: s.transition,
    opacity: s.isDragging ? 0.5 : undefined,
  };
  return (
    <Stack
      ref={s.setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      spacing={2}
      data-testid={`catalogue-row-${item.id}`}
      sx={{
        minHeight: 72,
        py: 1,
        borderBottom: "1px solid",
        borderColor: tokens.divider,
        bgcolor: tokens.surface,
      }}
    >
      <Box
        {...(draggable ? { ...s.attributes, ...s.listeners } : {})}
        aria-label={draggable ? `Drag ${item.name}` : undefined}
        sx={{
          display: "flex",
          color: tokens.inkFaint,
          cursor: draggable ? "grab" : "default",
          visibility: draggable ? "visible" : "hidden",
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Logo item={item} on={on} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }} noWrap>
          {item.name}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: tokens.inkMuted }} noWrap>
            {subtitle}
          </Typography>
        )}
      </Box>
      {trailing}
      <SegmentedChoice
        value={on ? "yes" : "no"}
        options={[
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ]}
        onChange={(v) => {
          if ((v === "yes") !== on) onToggle();
        }}
        testId={`catalogue-toggle-${item.id}`}
      />
    </Stack>
  );
}

/** The brand mark at row size: the logo, else the app's wordmark / letter. */
function Logo({ item, on }: { item: CatalogueItem; on: boolean }) {
  const lum = item.color ? luminance(item.color) : null;
  const ink = item.color && lum !== null && lum < 0.7 ? item.color : tokens.ink;
  const dim = { filter: on ? "none" : "grayscale(1)", opacity: on ? 1 : 0.45 };
  return (
    <Box
      sx={{
        width: 80,
        height: 40,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {item.logoUrl ? (
        <Box
          component="img"
          src={item.logoUrl}
          alt=""
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            ...dim,
          }}
        />
      ) : (
        <Typography
          sx={{
            color: ink,
            fontWeight: 800,
            fontSize: item.wordmark ? 13 : 22,
            textAlign: "center",
            lineHeight: 1.1,
            ...dim,
          }}
        >
          {item.wordmark || item.letter || item.name.slice(0, 1)}
        </Typography>
      )}
    </Box>
  );
}
